"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import * as LucideIcons from "lucide-react";
import { SkillCategory, Skill } from "@/types";

// Legacy image-based icons (for backwards compatibility)
const LegacyImageIcons: Record<string, string> = {
    flutter: "/icons/flutter.png",
    dart: "/icons/dart.png",
    firebase: "/icons/firebase.png",
    mysql: "/icons/mysql.png",
};

// Skill colors for hover states
const skillColors: Record<string, string> = {
    // Development
    code: "#6366F1",
    code2: "#8B5CF6",
    terminal: "#22C55E",
    database: "#336791",
    server: "#F97316",
    cloud: "#0EA5E9",
    globe: "#14B8A6",
    smartphone: "#EC4899",
    cpu: "#EF4444",

    // Design
    palette: "#F472B6",
    figma: "#F24E1E",
    pentool: "#A855F7",
    brush: "#F43F5E",
    image: "#06B6D4",
    layers: "#8B5CF6",
    layout: "#3B82F6",

    // Tools
    gitbranch: "#F05032",
    github: "#181717",
    package: "#CB3837",
    box: "#FFD43B",
    wrench: "#78716C",
    settings: "#6B7280",

    // Legacy
    flutter: "#02569B",
    dart: "#0175C2",
    firebase: "#FFCA28",
    git: "#F05032",
    java: "#ED8B00",
    python: "#3776AB",
    mysql: "#4479A1",
    stitch: "#6366F1",
    behance: "#1769FF",
};

// Helper to get Lucide icon component by name
function getLucideIcon(iconName: string): React.ComponentType<{ size?: number; className?: string }> | null {
    // Try exact match first
    const icons = LucideIcons as unknown as Record<string, React.ComponentType<{ size?: number; className?: string }>>;
    if (icons[iconName]) return icons[iconName];

    // Try with first letter uppercase
    const formattedName = iconName.charAt(0).toUpperCase() + iconName.slice(1);
    if (icons[formattedName]) return icons[formattedName];

    // Try converting kebab-case or snake_case to PascalCase
    const pascalCase = iconName
        .split(/[-_]/)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join('');
    if (icons[pascalCase]) return icons[pascalCase];

    return null;
}

// Get color for an icon
function getIconColor(iconName: string): string {
    const lowerName = iconName.toLowerCase().replace(/[-_]/g, '');
    return skillColors[lowerName] || "#6366F1"; // Default to indigo
}

interface SkillsSectionProps {
    categories: SkillCategory[];
}

export default function SkillsSection({ categories }: SkillsSectionProps) {
    return (
        <section
            id="skills"
            className="section-snap flex items-center bg-background min-h-screen py-20"
        >
            <div className="container mx-auto px-section-x">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="mb-16 text-center"
                >
                    <h2
                        className="text-section-title font-bold text-foreground text-center mb-4"
                        style={{ fontFamily: "var(--font-playfair), Georgia, serif" }}
                    >
                        Skills & Tools
                    </h2>
                    <p className="text-body text-text-secondary mt-4 text-center max-w-2xl mx-auto">
                        Technologies and tools I use to bring ideas to life
                    </p>
                </motion.div>

                {/* Skills Categories */}
                <div className="space-y-16">
                    {categories.map((category, categoryIndex) => (
                        <motion.div
                            key={category.name}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: categoryIndex * 0.1 }}
                        >
                            {/* Category Title */}
                            <h3 className="text-xl font-semibold text-foreground mb-8 flex items-center gap-3">
                                <span className="w-8 h-1 bg-accent rounded-full" />
                                {category.name}
                            </h3>

                            {/* Skills Grid */}
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                {category.skills.map((skill, skillIndex) => (
                                    <SkillCard
                                        key={skill.name}
                                        skill={skill}
                                        index={skillIndex}
                                    />
                                ))}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

interface SkillCardProps {
    skill: Skill;
    index: number;
}

function SkillCard({ skill, index }: SkillCardProps) {
    // Check if it's a custom uploaded icon
    const hasCustomIcon = Boolean(skill.customIcon);

    // Check if it's a legacy image icon (for backwards compatibility)
    const legacyImageIcon = LegacyImageIcons[skill.icon.toLowerCase()];
    const hasLegacyImage = Boolean(legacyImageIcon);

    // Try to get Lucide icon
    const LucideIcon = getLucideIcon(skill.icon);

    // Determine hover color
    const hoverColor = getIconColor(skill.icon);

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            whileHover={{
                scale: 1.05,
                y: -5,
            }}
            whileTap={{ scale: 0.95 }}
            className="group relative"
        >
            <div
                className="bg-surface hover:bg-surface-elevated active:bg-surface-elevated rounded-2xl p-6 flex flex-col items-center justify-center gap-3 transition-all duration-300 border border-border hover:border-accent/50 active:border-accent/50 hover:shadow-lg cursor-pointer min-h-[140px]"
                style={{
                    ["--hover-color" as string]: hoverColor,
                }}
            >
                {/* Icon */}
                <motion.div
                    whileHover={{ rotate: [0, -10, 10, 0] }}
                    transition={{ duration: 0.4 }}
                    className="text-text-secondary group-hover:text-[var(--hover-color)] transition-all duration-300"
                >
                    {hasCustomIcon ? (
                        // Custom uploaded icon
                        <Image
                            src={skill.customIcon!}
                            alt={skill.name}
                            width={32}
                            height={32}
                            className="w-8 h-8 object-contain grayscale group-hover:grayscale-0 transition-all duration-300"
                        />
                    ) : hasLegacyImage ? (
                        // Legacy image icon (backwards compatibility)
                        <Image
                            src={legacyImageIcon}
                            alt={skill.name}
                            width={32}
                            height={32}
                            className="w-8 h-8 object-contain grayscale group-hover:grayscale-0 transition-all duration-300"
                        />
                    ) : LucideIcon ? (
                        // Dynamic Lucide icon
                        <LucideIcon size={32} className="w-8 h-8" />
                    ) : (
                        // Fallback to Code icon
                        <LucideIcons.Code size={32} className="w-8 h-8" />
                    )}
                </motion.div>

                {/* Skill Name */}
                <span className="text-sm font-medium text-text-secondary group-hover:text-foreground transition-colors duration-300 text-center">
                    {skill.name}
                </span>

                {/* Hover Glow Effect */}
                <motion.div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    style={{
                        background: `radial-gradient(circle at center, ${hoverColor}10 0%, transparent 70%)`,
                    }}
                />
            </div>
        </motion.div>
    );
}
